/**
 * Servicio para límites por plan (productos y campañas).
 * Llama a las Cloud Functions getCompanyPlanLimits, createSolicitudProductoHTTP (fetch con CORS), createSolicitudCampana, createProduct, createCampaign.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { app, getCreateSolicitudProductoHTTPUrl, getCreateSolicitudCampanaHTTPUrl, getCreateProductHTTPUrl, getCreateCampaignHTTPUrl, getAdminSolicitudOpsHTTPUrl } from '../../../firebase';

const functions = getFunctions(app);
const auth = getAuth(app);

export async function getCompanyPlanLimits(companyId) {
  const fn = httpsCallable(functions, 'getCompanyPlanLimits');
  const res = await fn({ companyId });
  return res.data;
}

/**
 * @param {Object} payload - { empresaId, empresaNombre, nombre, descripcion, precio, categoria, marca, stock, url, imagenes, ... }
 * @returns {{ success: boolean, id: string }}
 * @throws {Error} code limit_reached_products
 */
export async function createSolicitudProducto(payload) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Debes iniciar sesión');
  const url = getCreateSolicitudProductoHTTPUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ payload }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Error al crear solicitud de producto');
    err.code = data.code || 'internal';
    if (data.code === 'limit_reached_products') err.code = 'resource-exhausted';
    throw err;
  }
  return { success: data.success, id: data.id };
}

/**
 * @param {Object} payload - { empresaId, empresaNombre, titulo, descripcion, fechaInicio, fechaFin, presupuesto, tipo, ... }
 * @returns {{ success: boolean, id: string }}
 * @throws {Error} code limit_reached_campaigns
 */
export async function createSolicitudCampana(payload) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Debes iniciar sesión');
  const url = getCreateSolicitudCampanaHTTPUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ payload }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Error al crear solicitud de campaña');
    err.code = data.code || 'internal';
    if (data.code === 'limit_reached_campaigns') err.code = 'resource-exhausted';
    throw err;
  }
  return { success: data.success, id: data.id };
}

/**
 * Crear producto en colección productos (admin al aprobar solicitud).
 * Usa HTTP con CORS para evitar bloqueos desde localhost.
 * @param {string} companyId
 * @param {Object} payload - campos del producto (nombre, descripcion, precio, imagenes, etc.)
 */
export async function createProduct(companyId, payload, options = {}) {
  const idToken = await auth.currentUser?.getIdToken(true);
  if (!idToken) throw new Error('Debes iniciar sesión');
  const url = getCreateProductHTTPUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ companyId, payload, ...options }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Error al crear producto');
    err.code = data.code || 'internal';
    if (data.code === 'limit_reached_products') err.code = 'resource-exhausted';
    throw err;
  }
  return { success: data.success, id: data.id };
}

/**
 * Crear campaña en colección campanas (admin al aprobar solicitud).
 * Usa HTTP con CORS para evitar bloqueos desde localhost.
 * @param {string} companyId
 * @param {Object} payload - titulo, descripcion, fechaInicio, fechaFin, presupuesto, tipo, estado, visible, etc.
 */
export async function createCampaign(companyId, payload, options = {}) {
  const idToken = await auth.currentUser?.getIdToken(true);
  if (!idToken) throw new Error('Debes iniciar sesión');
  const url = getCreateCampaignHTTPUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ companyId, payload, ...options }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Error al crear campaña');
    err.code = data.code || 'internal';
    if (data.code === 'limit_reached_campaigns') err.code = 'resource-exhausted';
    throw err;
  }
  return { success: data.success, id: data.id };
}

/**
 * Admin: operaciones sobre solicitudes (editar, rechazar, en_revision) via Cloud Function.
 * Evita writes directos a Firestore que requieren custom claims de admin en las reglas.
 */
export async function adminSolicitudOps(params) {
  const idToken = await auth.currentUser?.getIdToken(true);
  if (!idToken) throw new Error('Debes iniciar sesión');
  const url = getAdminSolicitudOpsHTTPUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(params),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Error en operación admin');
    err.code = data.code || 'internal';
    throw err;
  }
  return { success: data.success };
}
